import { Controller } from "@hotwired/stimulus"

// Tabbed panels, ported from intranet_dashwindR8_2025.
//
// Callers: the vehicle page's tabs (milestone 4) and the dashboard's Fleet /
// Issues / Activity (milestone 7).
//
// Markup contract (matches the intranet's, so tab markup ported from there
// works unchanged):
//   <div data-controller="tabs" data-tabs-active-tab-class="...">
//     <a data-tabs-target="tab"   data-tab-id="hours"   data-action="tabs#select">
//     <div data-tabs-target="panel" data-panel-id="hours">
//
// `this.data.get("active-tab-class")` is Stimulus's legacy DataMap API. Verified
// still present in @hotwired/stimulus 3.2.2 (dist/stimulus.js:2537 —
// `get data() { return this.scope.data }`), so it reads
// `data-tabs-active-tab-class`. It is deprecated in favour of `static classes`;
// left as-is on purpose, because changing the attribute contract now would
// silently break any tab markup copied over from the intranet later. Modernise
// it and the markup together, or not at all.
export default class extends Controller {
  static targets = ["tab", "panel"]
  static values = { initialTab: { type: String, default: "" } }

  connect() {
    // Initial tab comes from the enclosing turbo-frame's src, then the page
    // URL's ?tab=, then the data value — so a deep link and a frame reload both
    // land on the tab the URL names.
    const frame = this.element.closest('turbo-frame')
    const frameSrc = frame?.getAttribute('src') || ''
    const frameTab = frameSrc.includes('?') ? new URL(frameSrc, window.location.origin).searchParams.get("tab") : null
    const urlTab = new URL(window.location.href).searchParams.get("tab")
    const initialTabId = frameTab || urlTab || this.initialTabValue

    let initialTab = this.tabTargets[0]
    if (initialTabId) {
      const found = this.tabTargets.find(t => t.dataset.tabId === initialTabId)
      if (found) initialTab = found
    }

    this.selectTab(initialTab)
  }

  select(event) {
    event.preventDefault()
    this.selectTab(event.currentTarget)
  }

  // Split on any run of whitespace and drop empties. `classList.add("")` throws
  // `SyntaxError: The token provided must not be empty`, and this controller
  // exists so markup copied from the intranet works unchanged — which is
  // exactly the path that supplies a class string with a double or trailing
  // space. A throw here happens inside connect(), killing the controller and
  // leaving every panel hidden.
  get activeTabTokens() {
    return this.activeTabClass.split(/\s+/).filter(Boolean)
  }

  // Optional `data-tabs-inactive-tab-class`: classes every NON-selected tab
  // carries and the selected one loses. Needed whenever the inactive and
  // active states set the same property -- text colour here -- because
  // adding the active class over a standing inactive one leaves the cascade
  // to decide, and Tailwind's built order put `text-slate-*` after
  // `text-primary-*`, so the selected tab kept its grey (measured
  // 2026-09-13, both themes). Absent, the controller behaves exactly as the
  // intranet's: markup copied from there still works unchanged.
  get inactiveTabTokens() {
    return (this.data.get("inactive-tab-class") || "").split(/\s+/).filter(Boolean)
  }

  selectTab(selectedTab) {
    // No tab targets yet (an empty turbo-frame, a partial still loading) —
    // upstream threw here on `undefined.classList`, which kills connect().
    if (!selectedTab) return

    // aria-current says which tab is open, not only the colour; the server
    // renders it on the tab the URL names, and it follows each switch.
    this.tabTargets.forEach(tab => {
      tab.classList.remove(...this.activeTabTokens)
      if (this.inactiveTabTokens.length) tab.classList.add(...this.inactiveTabTokens)
      tab.removeAttribute("aria-current")
    })

    this.panelTargets.forEach(panel => {
      panel.classList.add("hidden")
    })

    if (this.inactiveTabTokens.length) selectedTab.classList.remove(...this.inactiveTabTokens)
    selectedTab.classList.add(...this.activeTabTokens)
    selectedTab.setAttribute("aria-current", "page")

    const panelId = selectedTab.dataset.tabId
    const selectedPanel = this.panelTargets.find(panel =>
      panel.dataset.panelId === panelId
    )

    if (selectedPanel) {
      selectedPanel.classList.remove("hidden")
    }
  }

  get activeTabClass() {
    return this.data.get("active-tab-class") || "after:opacity-100 text-primary-600"
  }
}
